// Produces a short, user-friendly message from an axios error.
//
// The API returns errors as { Status: "error", Msg: "..." } (capital Msg).
// Pages used to read err.response.data.message (lowercase), which is always
// undefined, so users saw axios's generic "Request failed with status code N".
// This helper reads the real message, maps known plain messages to localized
// text, and hides raw server/DB errors behind a friendly generic message.
//
// Usage:  this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) })

// Known plain backend messages -> i18n key under errors.api.*
const MESSAGE_KEYS = {
    "invalid id": "errors.api.invalid_id",
    "invalid request body": "errors.api.invalid_body",
    "name_th is required": "errors.api.name_th_required",
    "code is required": "errors.api.code_required",
    "price must be > 0": "errors.api.price_gt0",
    "sku price must be > 0": "errors.api.sku_price_gt0",
    "discount_value must be > 0 when discount_type is percent": "errors.api.discount_value_gt0",
    "category has active products; reassign them before deleting": "errors.api.category_has_products",
    "category with this name already exists in the same parent": "errors.api.category_name_dup",
    "template has active member coupons; cannot deactivate": "errors.api.coupon_template_in_use",
    "campaign not found": "errors.api.not_found",
    "category not found": "errors.api.not_found",
    "coupon template not found": "errors.api.not_found",
    "product not found": "errors.api.not_found",
    "promotion not found": "errors.api.not_found"
};

// Backend error codes (data.Msg) that carry a meaning the user must see even
// on a 5xx — checked BEFORE the generic server message. bo-api #834 / bo-fn #416:
// a disabled WMS product scanned in packing / inventory-in, and the toggle before
// the owner has run the is_active SQL (503).
const CODE_KEYS = {
    WMS_PRODUCT_INACTIVE: "wms_product_active.scan_inactive",
    WMS_PRODUCT_ACTIVE_COLUMN_MISSING: "wms_product_active.column_missing"
};

function translate(t, key, fallback) {
    if (typeof t === "function") {
        const tr = t(key);
        if (tr && tr !== key) return tr;
    }
    return fallback;
}

export function apiErrorMessage(err, t) {
    // No HTTP response = network / timeout / CORS.
    if (!err || !err.response) {
        return translate(t, "errors.api.network", "ไม่สามารถเชื่อมต่อระบบได้ กรุณาลองใหม่");
    }

    const status = err.response.status;
    const data = err.response.data || {};
    const raw = ((data.Msg || data.message || "") + "").trim();

    const codeKey = CODE_KEYS[raw];
    if (codeKey) {
        return translate(t, codeKey, raw);
    }

    // Server-side failures are technical (raw DB/transaction errors); never
    // expose them — show a friendly, actionable message instead.
    if (status >= 500) {
        return translate(t, "errors.api.server", "ระบบขัดข้อง กรุณาลองใหม่ หรือแจ้งผู้ดูแลระบบ");
    }
    if (!raw) {
        return translate(t, "errors.api.unknown", "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    }

    // Backend already returned an i18n key (e.g. errors.catalog.product_code_taken).
    if (raw.indexOf("errors.") === 0) {
        const key = raw.replace(/:\s*$/, "");
        const tr = translate(t, key, null);
        if (tr) return tr;
    }

    // Known plain message -> localized text.
    const mappedKey = MESSAGE_KEYS[raw];
    if (mappedKey) {
        return translate(t, mappedKey, raw);
    }

    // Otherwise the backend message is meaningful enough to show as-is.
    return raw;
}

export { CODE_KEYS as API_ERROR_CODE_KEYS };
export default apiErrorMessage;
